import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { agentService } from '../services/agentService'
import { Save, ArrowLeft } from 'lucide-react'

export function AgentBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = Boolean(id)
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '1.0.0',
    config: {
      agent: {
        name: '',
        description: '',
        llm: {
          provider: 'openai',
          model: 'gpt-4',
          temperature: 0.7,
          maxTokens: 2000
        },
        mcp: {
          enabled: false,
          tools: []
        },
        prompts: {
          system: 'Tu es un assistant IA utile et professionnel.',
          user: 'Comment puis-je t\'aider aujourd\'hui ?'
        },
        rtc: {
          enabled: true,
          signalingServer: import.meta.env.VITE_SIGNALING_WS_URL || 'ws://localhost:3000'
        },
        docker: {
          image: 'agentteamforge/agent-base:latest',
          resources: {
            memory: '512Mi',
            cpu: '500m'
          }
        }
      }
    }
  })

  const { data: agent, isLoading } = useQuery(
    ['agent', id],
    () => agentService.getAgentById(id!),
    {
      enabled: isEditing,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            name: data.name,
            description: data.description,
            version: data.version,
            config: data.config
          })
        }
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (isEditing) {
        await agentService.updateAgent(id!, formData)
      } else {
        await agentService.createAgent(formData)
      }
      navigate('/agents')
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    }
  }

  const handleConfigChange = (path: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev }
      const keys = path.split('.')
      let current: any = newData.config
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      
      current[keys[keys.length - 1]] = value
      return newData
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/agents')}
            className="flex items-center space-x-2 text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Modifier l\'agent' : 'Nouvel agent'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'agent
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value,
                    config: {
                      ...prev.config,
                      agent: {
                        ...prev.config.agent,
                        name: e.target.value
                      }
                    }
                  }))
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Configuration LLM</h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fournisseur
              </label>
              <select
                value={formData.config.agent.llm.provider}
                onChange={(e) => handleConfigChange('agent.llm.provider', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="local">Local</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modèle
              </label>
              <input
                type="text"
                value={formData.config.agent.llm.model}
                onChange={(e) => handleConfigChange('agent.llm.model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Température
              </label>
              <input
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={formData.config.agent.llm.temperature}
                onChange={(e) => handleConfigChange('agent.llm.temperature', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tokens maximum
              </label>
              <input
                type="number"
                min="1"
                max="4000"
                value={formData.config.agent.llm.maxTokens}
                onChange={(e) => handleConfigChange('agent.llm.maxTokens', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Prompts</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt système
              </label>
              <textarea
                value={formData.config.agent.prompts.system}
                onChange={(e) => handleConfigChange('agent.prompts.system', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt utilisateur
              </label>
              <textarea
                value={formData.config.agent.prompts.user}
                onChange={(e) => handleConfigChange('agent.prompts.user', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/agents')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="w-5 h-5" />
            <span>{isEditing ? 'Mettre à jour' : 'Créer'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}
